import type { TreatmentPlan, TreatmentStep } from '../../../lib/localDB';
import { STEP_DEFINITIONS, getStepStatus, recalcPlannedDates } from '../types';

type Props = {
  plan: TreatmentPlan;
  onToggleStep: (planId: string, stepId: string) => void;
};

const STATUS_CONFIG = {
  completed: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700', icon: 'ri-check-line' },
  overdue: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700', icon: 'ri-alarm-warning-line' },
  due_soon: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: 'ri-time-line' },
  upcoming: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500', badge: 'bg-gray-100 text-gray-500', icon: 'ri-calendar-line' },
};

const COLOR_VARIANTS: Record<string, string> = {
  teal: 'bg-teal-500',
  cyan: 'bg-cyan-500',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  orange: 'bg-orange-500',
  green: 'bg-green-500',
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function StepRow({
  step,
  def,
  index,
  isLast,
  today,
  onToggle,
  isLocked,
}: {
  step: TreatmentStep;
  def: (typeof STEP_DEFINITIONS)[0];
  index: number;
  isLast: boolean;
  today: string;
  onToggle: () => void;
  isLocked: boolean;
}) {
  const status = getStepStatus(step, today);
  const cfg = STATUS_CONFIG[status];

  return (
    <div className="flex gap-3">
      {/* Timeline connector */}
      <div className="flex flex-col items-center">
        <button
          type="button"
          onClick={onToggle}
          disabled={isLocked}
          className={`
            w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all border-2 cursor-pointer
            ${step.completed
              ? 'bg-green-500 border-green-500 text-white'
              : isLocked
              ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed'
              : `${cfg.border} ${cfg.bg} ${cfg.text} hover:scale-105`
            }
          `}
          title={isLocked ? 'Önceki adımı tamamlayın' : step.completed ? 'İşareti kaldır' : 'Tamamlandı olarak işaretle'}
        >
          <i className={`${step.completed ? 'ri-check-line' : def.icon} text-sm`}></i>
        </button>
        {!isLast && (
          <div className={`w-0.5 flex-1 min-h-[28px] mt-1 ${step.completed ? 'bg-green-300' : 'bg-gray-200'}`}></div>
        )}
      </div>

      {/* Step content */}
      <div className={`flex-1 pb-5 ${isLast ? 'pb-2' : ''}`}>
        <div className={`rounded-xl border p-3 transition-all ${step.completed ? 'border-green-200 bg-green-50' : `${cfg.border} ${cfg.bg}`}`}>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className={`w-4 h-4 rounded-full ${step.completed ? 'bg-green-400' : COLOR_VARIANTS[def.color] || 'bg-gray-400'} flex-shrink-0`}></div>
                <span className={`font-semibold text-sm ${step.completed ? 'text-green-800' : 'text-gray-800'}`}>
                  {def.label}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
                  {step.completed
                    ? 'Tamamlandı'
                    : status === 'overdue'
                    ? 'Gecikti'
                    : status === 'due_soon'
                    ? 'Bu Hafta'
                    : 'Bekliyor'
                  }
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                {step.completed && step.completed_date ? (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <i className="ri-check-double-line"></i>
                    Tamamlandı: {formatDate(step.completed_date)}
                  </span>
                ) : (
                  <span className={`text-xs flex items-center gap-1 ${cfg.text}`}>
                    <i className="ri-calendar-event-line"></i>
                    Planlanan: {formatDate(step.planned_date)}
                  </span>
                )}
              </div>
            </div>
            <span className="text-xs font-bold text-gray-400 bg-white px-2 py-1 rounded-lg border border-gray-100 whitespace-nowrap flex-shrink-0">
              #{index + 1}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TreatmentPlanCard({ plan, onToggleStep }: Props) {
  const today = new Date().toISOString().split('T')[0];
  const recalcedSteps = recalcPlannedDates(plan.steps);
  const completedCount = recalcedSteps.filter((s) => s.completed).length;
  const progressPct = Math.round((completedCount / recalcedSteps.length) * 100);

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
      {/* Header */}
      <div className={`px-5 py-4 ${plan.all_completed ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100' : 'bg-gray-50 border-b border-gray-100'}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan.all_completed ? 'bg-green-500' : 'bg-gradient-to-br from-teal-500 to-cyan-600'}`}>
              <i className={`${plan.all_completed ? 'ri-shield-check-line' : 'ri-tooth-line'} text-white text-lg`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-gray-900 text-sm">
                  {new Date(plan.operation_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })} Ameliyatı
                </span>
                {plan.all_completed && (
                  <span className="px-2 py-0.5 bg-green-500 text-white rounded-full text-xs font-bold flex items-center gap-1">
                    <i className="ri-check-double-line text-xs"></i>
                    Tedavi Tamamlandı
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{completedCount} / {recalcedSteps.length} adım tamamlandı</p>
            </div>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-2 min-w-[120px]">
            <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${plan.all_completed ? 'bg-green-500' : 'bg-gradient-to-r from-teal-500 to-cyan-500'}`}
                style={{ width: `${progressPct}%` }}
              ></div>
            </div>
            <span className="text-xs font-bold text-gray-700 w-9 text-right">{progressPct}%</span>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="p-5">
        {recalcedSteps.map((step, idx) => {
          const def = STEP_DEFINITIONS.find((d) => d.step_id === step.step_id)!;
          const prevCompleted = idx === 0 || recalcedSteps[idx - 1].completed;
          const isLocked = !prevCompleted && !step.completed;

          return (
            <StepRow
              key={step.step_id}
              step={step}
              def={def}
              index={idx}
              isLast={idx === recalcedSteps.length - 1}
              today={today}
              onToggle={() => onToggleStep(plan.id, step.step_id)}
              isLocked={isLocked}
            />
          );
        })}
      </div>
    </div>
  );
}
